/**
 * Rule: a SYSTEM step's attached action must not run with an empty required INPUT.
 *
 * `step-output-unbound` beside this one guards the value a step WRITES OUT. This guards the value
 * it SENDS IN, and nothing guarded it before: attaching an action seeds every one of its inputs
 * with an empty "Fixed Value" placeholder (`workflow-action-resolver.ts buildStepAction`), so a
 * parameter nobody ever filled is indistinguishable at a glance from one deliberately set to "".
 * The action still runs, the column still gets written, and what lands in it is nothing.
 *
 * Live `44125266` is the whole shape of it: 70 inputs across the process had no source, two repair
 * rounds closed 11, and the remaining 59 — the requester's material, quantity, need date, amount,
 * delivery location, every budget and stock result, both approval decisions — shipped empty. The
 * platform's own project validation said nothing, and the process linted clean. The user's ruling
 * (2026-09-15): leaving these to the model alone produces exactly that, so an empty parameter is
 * now an error on the diagram where it is visible.
 *
 * WHAT COUNTS AS EMPTY. The value lives on `parameter.symbol` and there are three ways to have
 * none: no symbol at all; the seeded placeholder, which carries `isFixed` with an empty
 * `fixedValue`; and a `fixed` symbol whose value was cleared. Anything else — a workflow
 * parameter, a process-instance value, a platform default, an action result, a real constant — is
 * a source, and none of them is questioned here.
 *
 * WHAT IS DELIBERATELY NOT REPORTED:
 *  - an input the catalog marks OPTIONAL (`isOptionalParameter`). The action runs without it, and
 *    the build already passes five of them over by design on a normal process;
 *  - a sub-parameter of an object input whose PARENT carries a value — the runtime uses the
 *    parent's object, exactly as `ValidationExtensions` reasons about the same shape;
 *  - a USER task. Its action inputs are written by the assignee's form and its buttons, which is
 *    the same reason `system-step-has-action` and `step-output-unbound` scope to `bpmn:Task`.
 *
 * CONSERVATIVE, like both siblings: when the workflow context is not ready or the JSON step cannot
 * be resolved, SKIP. This is an error-level rule and must never fire a false positive mid-load;
 * the linter re-runs on the next diagram event.
 */
const { is } = require("bpmnlint-utils");

const ESYMBOL_CATEGORY_NONE = 0;
const ESYMBOL_CATEGORY_FIXED = 4;

function resolveStep(ctx, nodeId) {
  const sd = ctx && ctx.providerInstance ? ctx.providerInstance : null;
  if (!sd) return { ready: false, step: null };

  const workflowProvider = sd.workflowProvider ? sd.workflowProvider : null;
  const ctxValues =
    typeof sd.getScreenDesignContextProviderValues === "function" ? sd.getScreenDesignContextProviderValues() : null;
  const selectedWf =
    (workflowProvider && typeof workflowProvider.getSelectedWf === "function" && workflowProvider.getSelectedWf()) ||
    (ctxValues && ctxValues.selectedWf) ||
    null;
  if (!selectedWf) return { ready: false, step: null };

  let step = null;
  if (workflowProvider && typeof workflowProvider.findStepRecursively === "function") {
    try {
      step = workflowProvider.findStepRecursively(selectedWf, (s) => s && s.id === nodeId);
    } catch {
      step = null;
    }
  }
  if (!step) {
    const steps = ctx && typeof ctx.getWfSteps === "function" ? ctx.getWfSteps() : undefined;
    if (Array.isArray(steps)) step = steps.find((s) => s && s.id === nodeId) || null;
  }
  return { ready: true, step };
}

/** A parameter carries a value when its symbol names one. See WHAT COUNTS AS EMPTY above. */
function hasNoValue(symbol) {
  if (!symbol) return true;
  const category = symbol.category;
  const blankFixed =
    symbol.fixedValue === undefined || symbol.fixedValue === null || String(symbol.fixedValue).length === 0;
  // The seed writes `isFixed` with an empty value; its category has never been the fixed one, so
  // the flag is what identifies it rather than the number.
  if (symbol.isFixed === true && blankFixed) return true;
  if (category === undefined || category === null) return true;
  if (category === ESYMBOL_CATEGORY_NONE) return true;
  if (category === ESYMBOL_CATEGORY_FIXED) return blankFixed;
  return false;
}

/** The label to name in the message — what the panel calls this input. */
function parameterLabel(parameter) {
  if (parameter.displayName && String(parameter.displayName).trim()) return String(parameter.displayName).trim();
  return String(parameter.name || "").trim();
}

function unboundInputsOf(action) {
  const parameters = Array.isArray(action.parameters) ? action.parameters : [];
  const out = [];
  for (const parameter of parameters) {
    if (!parameter || parameter.isOptionalParameter === true) continue;
    if (!hasNoValue(parameter.symbol)) continue;
    // An object input whose children carry the values is bound through them.
    const children = Array.isArray(parameter.parameters) ? parameter.parameters : [];
    if (children.some((child) => child && child.isOptionalParameter !== true && !hasNoValue(child.symbol))) continue;
    const label = parameterLabel(parameter);
    if (label) out.push(label);
  }
  return out;
}

module.exports = function stepActionInputUnbound({ ctx }) {
  return {
    check(node, reporter) {
      if (!node) return;
      const bo = node.businessObject || node;
      if (bo.$type !== "bpmn:Task") return;
      if (is(node, "bpmn:SubProcess") || is(node, "bpmn:CallActivity")) return;

      const { ready, step } = resolveStep(ctx, node.id);
      if (!ready || !step) return;

      const actions = Array.isArray(step.actions) ? step.actions : [];
      for (const action of actions) {
        if (!action) continue;
        const unbound = unboundInputsOf(action);
        if (unbound.length === 0) continue;
        const actionLabel = String(action.label || action.description || "").trim();
        const named = actionLabel ? `"${actionLabel}"` : "this step's action";
        reporter.report(
          node.id,
          `${named} runs with no value for ${unbound.join(", ")}. ` +
            `Every run writes ${unbound.length === 1 ? "that field" : "those fields"} empty. ` +
            `Give each one a source in the step's action panel, or remove it if the action does not need it.`
        );
      }
    }
  };
};
